/* eslint-disable no-param-reassign */
import {
	onBeforeUnmount, Ref, ref, watch,
} from 'vue';
import { EventBus } from '@global-js/event-bus';
import { throttle } from '@utils/utils';

type UseResizeCollapse = {
	isOpen: Ref<boolean>;
	toggleCollapse: () => void;
	beforeEnter: (el: HTMLElement) => void;
	enter: (el: HTMLElement) => void;
	beforeLeave: (el: HTMLElement) => void;
	leave: (el: HTMLElement) => void;
}

const resizeCollapse = (el: HTMLElement): void => {
	el.style.height = `${el.scrollHeight}px`;
};

const attachListener = (element: HTMLElement): (() => void) => {
	const throttledResizeCollapse = throttle(() => resizeCollapse(element), 250);
	EventBus.$on('window:resize', throttledResizeCollapse);
	return throttledResizeCollapse;
};

const detachListener = (listener: () => void): void => {
	EventBus.$off('window:resize', listener);
};

export const useResizeCollapse = (templateReference: Ref<HTMLElement | null> = ref(null)): UseResizeCollapse => {
	const isOpen = ref(false);
	let listener: (() => void) | null = null;

	const toggleCollapse = () => {
		isOpen.value = !isOpen.value;
	};

	watch(templateReference, (newVal, oldVal) => {
		if (newVal !== oldVal && newVal) {
			resizeCollapse(newVal);
			listener = attachListener(newVal);
		}
	}, { immediate: true });

	onBeforeUnmount(() => {
		if (listener) {
			detachListener(listener);
		}
	});

	const beforeEnter = (el: HTMLElement): void => {
		el.style.height = '0';
	};

	const enter = (el: HTMLElement): void => {
		el.style.height = `${el.scrollHeight}px`;
	};

	const beforeLeave = (el: HTMLElement): void => {
		el.style.height = `${el.scrollHeight}px`;
	};

	const leave = (el: HTMLElement): void => {
		el.style.height = '0';
	};

	return {
		isOpen,
		toggleCollapse,
		beforeEnter,
		enter,
		beforeLeave,
		leave
	};
};
